import i18n from 'i18next'
import type { AdminSideBarRouteProps } from 'ui/partials/AdminSidebar'
import type {
  BankTransfer,
  CallMethodProps,
  DepartmentSettingsProps,
  MedicalInstitutionProps,
} from 'types/med'
import type { DepartmentListItemProps } from 'types/common'
import { originUrl } from 'config/url'
import { ApiErrorWithMessage } from '../client'

// NOTE: propsに渡す高階関数
export const sideBarRoute =
  (navigateFunc: (path: string) => void) =>
  (current?: AdminSideBarRouteProps) =>
  (target: AdminSideBarRouteProps) => {
    if (target !== current) {
      navigateFunc(`/medical/${target}`)
    }
  }

export const emptyBankTransferSettings: BankTransfer = {
  bank_name: '',
  bank_number: '',
  branch_name: '',
  branch_number: '',
  account_type: '普通',
  account_number: '',
  account_name: '',
  payment_deadline: '14',
}

export const newDepartmentSettings: DepartmentSettingsProps = {
  name: '名称未設定',
  is_open: false,
  supplementary_text: '未設定',
  reservable_period: 7,
  reservation_deadline_timing: 'just_before',
  reservation_frame_start_time: '08:00',
  reservation_frame_end_time: '20:00',
  reservation_frame_period_minutes: 30,
  regular_reservation_settings: [],
  individual_reservation_settings: [],
  close_date_settings: [],
  free_description_field_setting: 'optional',
  free_description_supplement:
    '気になることや伝えたいことがあれば記入してください。',
  attachment_image_setting: 'optional',
  attachment_image_supplement:
    '共有したい画像やファイルがあれば添付してください。最大5点まで添付可能です。',
  use_in_person_consultation: false,
  call_methods: [
    {
      name: 'phone',
      label: '電話',
      is_active: true,
    },
    // TODO: ビデオ通話アプリ以外の通話方法が増えた時はここに追加
    // NOTE: ビデオ通話アプリの設定はここに含めない
  ],
  applicant_address_setting: 'optional',
  registration_card_number_setting: 'optional',
  insurance_card_setting: 'required',
  certificate_setting: 'optional',
  identification_document_setting: 'not',
  settlement_settings: [
    {
      label: 'クレジットカード',
      name: 'credit_card',
      is_active: false,
    },
    {
      name: 'bank_transfer',
      is_active: false,
      label: '銀行振込',
    },
    {
      name: 'next_visit',
      is_active: true,
      label: '対面時窓口',
    },
  ],
}

/**
 * NOTE: ビデオ通話アプリ接続uniqueエラーオブジェクト生成 ここだけ特殊な形式になっている
 * https://docs.google.com/spreadsheets/d/1nDcRQl8AB39GesZnmEzZiK5Q1Aie5hHXWQNPJG-FNcs/edit#gid=1339924295&range=D558
 */
export const videoAppUniqueError: (data: {
  med: MedicalInstitutionProps
  list: DepartmentListItemProps[]
  error: ApiErrorWithMessage
}) =>
  | {
      [key: string]: {
        type: string
        message: string
      }
    }
  | undefined = ({ med, list, error }) => {
  let key: string | undefined
  let label: 'Zoom' | 'Teams' | undefined
  if (
    error.has({
      key: 'zoom_account',
    })
  ) {
    key = 'zoom_account'
    label = 'Zoom'
  } else if (
    error.has({
      key: 'teams_account',
    })
  ) {
    key = 'teams_account'
    label = 'Teams'
  }

  if (key && label) {
    const arr = error.body?.values?.[key]?.[0]?.split(' ') // NOTE: "unique {医療機関ID} {受付科目ID}"になっている
    if (arr.length === 3) {
      const [type, medId, departmentId] = arr
      const isSameMed = Number(medId) === med.id
      const departmentName = list.find(
        (d) => d.id === Number(departmentId)
      )?.name
      return {
        [key]: {
          type: String(type),
          message: i18n.t(
            'errors.{{label}}アカウントはすでに{{name}}に接続されています。接続を解除するか、新しい{{label}}アカウントを設定してください。新しいアカウントを接続する場合は、必ず一度{{label}}アカウントをログアウトしてから再度接続してください。',
            {
              label,
              name: isSameMed
                ? `「${departmentName}」`
                : i18n.t('別のYaDoc Quick'),
            }
          ),
        },
      }
    }
  }
  return undefined
}

export const removeVideoAppSettings: (
  callMethods: CallMethodProps[]
) => CallMethodProps[] = (callMethods) => {
  return callMethods.filter((m) => !m.name.startsWith('video_'))
}

export const createLInkJsFilePath = `${originUrl}/tools/YaDocQuickFloatingLinkButton.js`
